import React, { useState } from 'react';
import { Table, Button, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doctorService } from '../../services';
import { Doctor } from '../../types';
import DoctorModal from '../../components/DoctorModal';
import './DoctorListPage.css';

const DoctorListPage: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const queryClient = useQueryClient();

  const { data: doctors, isLoading } = useQuery({
    queryKey: ['doctors'],
    queryFn: () => doctorService.getList(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => doctorService.delete(id),
    onSuccess: () => {
      message.success('Xóa bác sĩ thành công!');
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
    },
    onError: () => {
      message.error('Xóa bác sĩ thất bại!');
    },
  });

  const handleAdd = () => {
    setEditingDoctor(null);
    setModalVisible(true);
  };

  const handleEdit = (doctor: Doctor) => {
    setEditingDoctor(doctor);
    setModalVisible(true);
  };

  const handleDelete = (id: string | undefined) => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingDoctor(null);
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: 'Tên bác sĩ',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Số CCHN',
      dataIndex: 'license_number',
      key: 'license_number',
    },
    {
      title: 'Điện thoại',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'User trên ĐTQG',
      dataIndex: 'national_rx_user',
      key: 'national_rx_user',
    },
    {
      title: 'Tùy chọn',
      key: 'actions',
      width: 120,
      render: (_: any, record: Doctor) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#385da4' }}
          />
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa bác sĩ này?"
            onConfirm={() => handleDelete(record.id?.toString() || undefined)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tableData = doctors?.map((doctor, index) => ({
    ...doctor,
    index: index + 1,
    key: doctor.id || `doctor-${index}`,
  }));

  return (
    <div className="doctor-list-page">
      <div className="page-header">
        <h1 className="page-title">Danh sách bác sĩ</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Thêm bác sĩ
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} bác sĩ`,
        }}
      />

      <DoctorModal
        visible={modalVisible}
        doctor={editingDoctor}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default DoctorListPage;

